import React, { useState, useEffect } from 'react';
import { _T, C, CSS } from '../../shared/theme';
import { s_TI } from '../../shared/styles';
import { getSettings, saveSettings, testSlack, getBranding, saveBranding } from './businessApi';

const DAYS = [
  { v: 1, nl: 'Maandag',   en: 'Monday' },
  { v: 2, nl: 'Dinsdag',   en: 'Tuesday' },
  { v: 3, nl: 'Woensdag',  en: 'Wednesday' },
  { v: 4, nl: 'Donderdag', en: 'Thursday' },
  { v: 5, nl: 'Vrijdag',   en: 'Friday' },
  { v: 6, nl: 'Zaterdag',  en: 'Saturday' },
  { v: 0, nl: 'Zondag',    en: 'Sunday' },
];

const COLOR_PRESETS = ['#5B9ED0','#4AC87A','#E67E22','#9B59B6','#E74C3C','#1ABC9C','#F39C12','#2C3E50'];

export default function BusinessSettingsScreen({ lang, businessId }) {
  const nl = lang === 'nl';
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [testingSlack, setTestingSlack] = useState(false);
  const [slackStatus, setSlackStatus] = useState(null); // 'ok' | 'err'
  const [testingTeams, setTestingTeams] = useState(false);
  const [teamsStatus, setTeamsStatus] = useState(null);
  const [branding, setBranding] = useState({ primary_color: '#5B9ED0', secondary_color: '#4AC87A', company_tagline: '' });
  const [brandingSaved, setBrandingSaved] = useState(false);

  useEffect(() => {
    setLoading(true);
    Promise.all([
      getSettings(businessId).catch(() => ({})),
      getBranding(businessId).catch(() => ({})),
    ]).then(([s, b]) => {
      setSettings(s || {});
      if (b && Object.keys(b).length > 0) setBranding({ primary_color: b.primary_color || '#5B9ED0', secondary_color: b.secondary_color || '#4AC87A', company_tagline: b.company_tagline || '' });
      setLoading(false);
    });
  }, [businessId]);

  const handle = (key, val) => setSettings(s => ({ ...s, [key]: val }));

  const handleSave = async () => {
    setSaving(true);
    await saveSettings(businessId, settings);
    setSaving(false);
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  };

  const handleTestSlack = async () => {
    if (!settings?.slack_webhook) return;
    setTestingSlack(true); setSlackStatus(null);
    const res = await testSlack(businessId, { webhook: settings.slack_webhook });
    setSlackStatus(res.ok ? 'ok' : 'err');
    setTestingSlack(false);
  };

  const handleTestTeams = async () => {
    if (!settings?.teams_webhook) return;
    setTestingTeams(true); setTeamsStatus(null);
    const res = await testSlack(businessId, { webhook: settings.teams_webhook });
    setTeamsStatus(res.ok ? 'ok' : 'err');
    setTestingTeams(false);
  };

  const handleSaveBranding = async () => {
    await saveBranding(businessId, branding);
    setBrandingSaved(true);
    setTimeout(() => setBrandingSaved(false), 2000);
  };

  if (loading) return <div style={{ color: C.silver, textAlign: 'center', padding: 32, fontSize: 13 }}>...</div>;

  return (
    <div style={{ padding: '0 0 32px' }}>
      <style>{CSS}</style>

      {/* Feature 8: Scheduled Reminder Campaigns */}
      <div style={{ background: _T.bg3, border: `1px solid ${_T.cardBorderBot}`, borderRadius: 16, padding: '16px', marginBottom: 16 }}>
        <div style={{ fontSize: 9, color: _T.accent, letterSpacing: 3, fontWeight: 700, marginBottom: 14 }}>{nl ? 'HERINNERING CAMPAGNE' : 'REMINDER CAMPAIGN'}</div>
        <div style={{ color: C.silver, fontSize: 12, lineHeight: 1.5, marginBottom: 14 }}>
          {nl ? 'Stuur automatisch een check-in herinnering naar je team op een vaste dag en tijd.' : 'Automatically send a check-in reminder to your team on a set day and time.'}
        </div>

        <div style={{ marginBottom: 12 }}>
          <div style={{ color: C.silver, fontSize: 11, marginBottom: 8 }}>{nl ? 'Dag van de week' : 'Day of week'}</div>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {DAYS.map(d => (
              <button key={d.v} onClick={() => handle('reminder_day', settings.reminder_day === d.v ? null : d.v)}
                style={{ background: settings.reminder_day === d.v ? `${_T.accent}20` : _T.bg4, border: `1.5px solid ${settings.reminder_day === d.v ? _T.accent : _T.cardBorderBot}`, borderRadius: 10, color: settings.reminder_day === d.v ? _T.accent : C.silver, fontSize: 11, padding: '7px 10px', cursor: 'pointer', transition: 'all 0.15s' }}>
                {nl ? d.nl.slice(0, 3) : d.en.slice(0, 3)}
              </button>
            ))}
          </div>
        </div>

        <div>
          <div style={{ color: C.silver, fontSize: 11, marginBottom: 6 }}>{nl ? 'Tijd' : 'Time'}</div>
          <input type="time" value={settings.reminder_time || ''} onChange={e => handle('reminder_time', e.target.value)}
            style={{ ...s_TI({ width: '100%', boxSizing: 'border-box' }), fontSize: 14 }} />
        </div>

        {settings.reminder_day != null && settings.reminder_time && (
          <div style={{ marginTop: 12, background: `${_T.accent}10`, border: `1px solid ${_T.accent}33`, borderRadius: 10, padding: '10px 12px', fontSize: 12, color: _T.accent }}>
            {nl
              ? `📱 Herinnering verstuurd elke ${DAYS.find(d => d.v === settings.reminder_day)?.nl} om ${settings.reminder_time}`
              : `📱 Reminder sent every ${DAYS.find(d => d.v === settings.reminder_day)?.en} at ${settings.reminder_time}`}
          </div>
        )}
      </div>

      {/* Auto weekly report toggle */}
      <div style={{ background: _T.bg3, border: `1px solid ${_T.cardBorderBot}`, borderRadius: 16, padding: '14px 16px', marginBottom: 16 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <div style={{ color: C.white, fontSize: 13, fontWeight: 600 }}>{nl ? 'Automatisch weekrapport' : 'Auto weekly report'}</div>
            <div style={{ color: C.silver, fontSize: 11, marginTop: 2 }}>{nl ? 'Elke maandag rapport naar Slack/Teams sturen' : 'Send report to Slack/Teams every Monday'}</div>
          </div>
          <button onClick={() => handle('auto_report', settings.auto_report ? 0 : 1)}
            style={{ width: 44, height: 24, borderRadius: 12, background: settings.auto_report ? `linear-gradient(135deg,${_T.grad1},${_T.grad2})` : _T.bg4, border: `1px solid ${settings.auto_report ? _T.accent : _T.cardBorderBot}`, cursor: 'pointer', position: 'relative', transition: 'all 0.2s', flexShrink: 0 }}>
            <div style={{ position: 'absolute', top: 3, left: settings.auto_report ? 22 : 3, width: 16, height: 16, borderRadius: '50%', background: settings.auto_report ? '#0a1220' : C.silver, transition: 'left 0.2s' }} />
          </button>
        </div>
      </div>

      {/* Feature 10: Slack Integration */}
      <div style={{ background: _T.bg3, border: `1px solid ${_T.cardBorderBot}`, borderRadius: 16, padding: '16px', marginBottom: 16 }}>
        <div style={{ fontSize: 9, color: _T.accent, letterSpacing: 3, fontWeight: 700, marginBottom: 14 }}>{nl ? 'SLACK INTEGRATIE' : 'SLACK INTEGRATION'}</div>
        <div style={{ color: C.silver, fontSize: 12, lineHeight: 1.5, marginBottom: 12 }}>
          {nl ? 'Verbind je Slack workspace. Maak een Incoming Webhook aan via api.slack.com en plak de URL hieronder.' : 'Connect your Slack workspace. Create an Incoming Webhook at api.slack.com and paste the URL below.'}
        </div>
        <input value={settings.slack_webhook || ''} onChange={e => handle('slack_webhook', e.target.value)}
          placeholder="https://hooks.slack.com/services/..."
          style={{ ...s_TI({ width: '100%', boxSizing: 'border-box' }), fontSize: 12, marginBottom: 10 }} />
        <button onClick={handleTestSlack} disabled={testingSlack || !settings.slack_webhook}
          style={{ background: slackStatus === 'ok' ? `${C.teal}20` : slackStatus === 'err' ? `${C.red}20` : _T.bg4, border: `1px solid ${slackStatus === 'ok' ? C.teal : slackStatus === 'err' ? C.red : _T.cardBorderBot}`, borderRadius: 10, color: slackStatus === 'ok' ? C.teal : slackStatus === 'err' ? C.red : C.silver, fontSize: 12, fontWeight: 600, padding: '9px 16px', cursor: (testingSlack || !settings.slack_webhook) ? 'default' : 'pointer' }}>
          {testingSlack ? '...' : slackStatus === 'ok' ? (nl ? '✓ Verbonden!' : '✓ Connected!') : slackStatus === 'err' ? (nl ? '✕ Mislukt' : '✕ Failed') : (nl ? 'Test verbinding' : 'Test connection')}
        </button>
      </div>

      {/* Microsoft Teams Integration */}
      <div style={{ background: _T.bg3, border: `1px solid ${_T.cardBorderBot}`, borderRadius: 16, padding: '16px', marginBottom: 20 }}>
        <div style={{ fontSize: 9, color: _T.accent, letterSpacing: 3, fontWeight: 700, marginBottom: 14 }}>{nl ? 'MICROSOFT TEAMS INTEGRATIE' : 'MICROSOFT TEAMS INTEGRATION'}</div>
        <div style={{ color: C.silver, fontSize: 12, lineHeight: 1.5, marginBottom: 12 }}>
          {nl ? 'Verbind Microsoft Teams via een Incoming Webhook connector in je kanaal.' : 'Connect Microsoft Teams via an Incoming Webhook connector in your channel.'}
        </div>
        <input value={settings.teams_webhook || ''} onChange={e => handle('teams_webhook', e.target.value)}
          placeholder="https://outlook.office.com/webhook/..."
          style={{ ...s_TI({ width: '100%', boxSizing: 'border-box' }), fontSize: 12, marginBottom: 10 }} />
        <button onClick={handleTestTeams} disabled={testingTeams || !settings.teams_webhook}
          style={{ background: teamsStatus === 'ok' ? `${C.teal}20` : teamsStatus === 'err' ? `${C.red}20` : _T.bg4, border: `1px solid ${teamsStatus === 'ok' ? C.teal : teamsStatus === 'err' ? C.red : _T.cardBorderBot}`, borderRadius: 10, color: teamsStatus === 'ok' ? C.teal : teamsStatus === 'err' ? C.red : C.silver, fontSize: 12, fontWeight: 600, padding: '9px 16px', cursor: (testingTeams || !settings.teams_webhook) ? 'default' : 'pointer' }}>
          {testingTeams ? '...' : teamsStatus === 'ok' ? (nl ? '✓ Verbonden!' : '✓ Connected!') : teamsStatus === 'err' ? (nl ? '✕ Mislukt' : '✕ Failed') : (nl ? 'Test verbinding' : 'Test connection')}
        </button>
      </div>

      {/* White-label Branding */}
      <div style={{ background: _T.bg3, border: `1px solid ${_T.cardBorderBot}`, borderRadius: 16, padding: '16px', marginBottom: 16 }}>
        <div style={{ fontSize: 9, color: _T.accent, letterSpacing: 3, fontWeight: 700, marginBottom: 14 }}>{nl ? 'WHITE-LABEL BRANDING' : 'WHITE-LABEL BRANDING'}</div>
        <div style={{ color: C.silver, fontSize: 12, lineHeight: 1.5, marginBottom: 14 }}>
          {nl ? 'Pas de hoofdkleur en bedrijfsslogan aan voor een persoonlijkere ervaring.' : 'Customize the primary color and company tagline for a branded experience.'}
        </div>

        {/* Primary color */}
        <div style={{ marginBottom: 14 }}>
          <div style={{ color: C.silver, fontSize: 11, marginBottom: 8 }}>{nl ? 'Primaire kleur' : 'Primary color'}</div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 8 }}>
            {COLOR_PRESETS.map(c => (
              <button key={c} onClick={() => setBranding(p => ({ ...p, primary_color: c }))}
                style={{ width: 32, height: 32, borderRadius: 8, background: c, border: branding.primary_color === c ? `3px solid ${C.white}` : `2px solid transparent`, cursor: 'pointer', transition: 'transform 0.1s', transform: branding.primary_color === c ? 'scale(1.15)' : 'scale(1)' }} />
            ))}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{ width: 28, height: 28, borderRadius: 8, background: branding.primary_color, border: `2px solid ${_T.cardBorderBot}` }} />
            <input value={branding.primary_color} onChange={e => setBranding(p => ({ ...p, primary_color: e.target.value }))}
              style={{ ...s_TI({ flex: 1, fontSize: 12 }) }} placeholder="#5B9ED0" />
          </div>
        </div>

        {/* Secondary color */}
        <div style={{ marginBottom: 14 }}>
          <div style={{ color: C.silver, fontSize: 11, marginBottom: 8 }}>{nl ? 'Secundaire kleur' : 'Secondary color'}</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{ width: 28, height: 28, borderRadius: 8, background: branding.secondary_color, border: `2px solid ${_T.cardBorderBot}` }} />
            <input value={branding.secondary_color} onChange={e => setBranding(p => ({ ...p, secondary_color: e.target.value }))}
              style={{ ...s_TI({ flex: 1, fontSize: 12 }) }} placeholder="#4AC87A" />
          </div>
        </div>

        {/* Company tagline */}
        <div style={{ marginBottom: 14 }}>
          <div style={{ color: C.silver, fontSize: 11, marginBottom: 6 }}>{nl ? 'Bedrijfsslogan' : 'Company tagline'}</div>
          <input value={branding.company_tagline || ''} onChange={e => setBranding(p => ({ ...p, company_tagline: e.target.value }))}
            placeholder={nl ? 'Bijv. "Samen sterk voor jouw welzijn"' : 'E.g. "Together for your wellbeing"'}
            style={{ ...s_TI({ width: '100%', boxSizing: 'border-box', fontSize: 12 }) }} />
        </div>

        {/* Preview */}
        <div style={{ background: `${branding.primary_color}15`, border: `1px solid ${branding.primary_color}44`, borderRadius: 12, padding: '12px 14px', marginBottom: 12 }}>
          <div style={{ fontSize: 11, color: branding.primary_color, fontWeight: 700 }}>{nl ? 'Voorbeeld' : 'Preview'}</div>
          <div style={{ fontSize: 13, color: C.white, marginTop: 4, fontWeight: 600 }}>SenseLifting</div>
          {branding.company_tagline && <div style={{ fontSize: 11, color: C.silver, marginTop: 2 }}>{branding.company_tagline}</div>}
          <div style={{ marginTop: 8, display: 'flex', gap: 8 }}>
            <div style={{ background: branding.primary_color, borderRadius: 8, padding: '6px 12px', fontSize: 11, color: '#fff', fontWeight: 600 }}>{nl ? 'Knop' : 'Button'}</div>
            <div style={{ background: branding.secondary_color, borderRadius: 8, padding: '6px 12px', fontSize: 11, color: '#fff', fontWeight: 600 }}>{nl ? 'Accent' : 'Accent'}</div>
          </div>
        </div>

        <button onClick={handleSaveBranding}
          style={{ width: '100%', background: brandingSaved ? `${C.teal}20` : `${branding.primary_color}22`, border: `1px solid ${brandingSaved ? C.teal : branding.primary_color}66`, borderRadius: 12, color: brandingSaved ? C.teal : branding.primary_color, fontSize: 13, fontWeight: 600, padding: '10px', cursor: 'pointer' }}>
          {brandingSaved ? (nl ? '✓ Branding opgeslagen' : '✓ Branding saved') : (nl ? 'Branding opslaan' : 'Save branding')}
        </button>
      </div>

      {/* Save */}
      <button onClick={handleSave} disabled={saving}
        style={{ width: '100%', background: saved ? `${C.teal}20` : `linear-gradient(135deg,${_T.grad1},${_T.grad2})`, border: saved ? `1px solid ${C.teal}` : 'none', borderRadius: 14, color: saved ? C.teal : '#0a1220', fontSize: 15, fontWeight: 700, padding: '14px', cursor: saving ? 'default' : 'pointer', transition: 'all 0.2s' }}>
        {saving ? '...' : saved ? (nl ? '✓ Opgeslagen' : '✓ Saved') : (nl ? 'Instellingen opslaan' : 'Save settings')}
      </button>
    </div>
  );
}
